import { USERS_COLLECTION } from "../../constants/collections";
import { Config, StatusOption } from "./config";

const ROLES = {
  ADMIN: "admin",
  COURIER: "courier",
  CUSTOMER: "customer",
  SENDER: "sender",
};

const allRoles = [ROLES.ADMIN, ROLES.COURIER, ROLES.CUSTOMER, ROLES.SENDER];
const managementRoles = [ROLES.ADMIN, ROLES.COURIER];
const senderManagementRoles = [ROLES.ADMIN, ROLES.COURIER, ROLES.SENDER];

const STATUS = {
  NEW: "Новая",
  NOT_DELIVERED: "Не доставлено",
  DELIVERED: "Доставлено",
};

const ZONES = [
  "Не определена",
  "Зона 1",
  "Зона 2",
  "Зона 3",
  "Зона 4",
  "Зона 5",
];

const PRIORITY_OPTIONS = ["normal", "urgent", "after_19"];
const MANAGER_CONTACT = {
  HOME: "At Home",
  NOT_HOME: "Not At Home",
  NOT_CONTACTED: "Not Contacted",
};
const MANAGER_CONTACT_FIELD_NAME = "managerAlreadyCalledClient" as const;
const MANAGER_CONTACT_OPTIONS = [
  MANAGER_CONTACT.NOT_CONTACTED,
  MANAGER_CONTACT.HOME,
  MANAGER_CONTACT.NOT_HOME,
];
const MANAGER_CONTACT_COLOR_RULES = [
  { text: MANAGER_CONTACT.HOME, color: "#16a34a" },
  { text: MANAGER_CONTACT.NOT_HOME, color: "#d32f2f" },
  { text: MANAGER_CONTACT.NOT_CONTACTED, color: "#6b7280" },
];
const DELIVERY_STATUS_VALUES = ["new", "not_delivered", "delivered"] as const;
const SENDER_OPTIONS = [
  "Кидсстор",
  "Витамин 1",
  "Витамин 2",
  "Алматы",
  "Абылайхана",
  "Новый",
];

const statusOptions: StatusOption[] = [
  {
    value: "new",
    editableBy: managementRoles,
    visibleBy: allRoles,
  },
  {
    value: "not_delivered",
    editableBy: managementRoles,
    visibleBy: allRoles,
  },
  {
    value: "delivered",
    editableBy: managementRoles,
    visibleBy: allRoles,
  },
];

const STATUS_COLORS: Record<string, string> = {
  new: "#9333ea",
  not_delivered: "#dc2626",
  delivered: "#16a34a",
};

const ZONE_EXAMPLES = [
  "Зона 1: Бектурова 1 -> Анет баба 9/2 (28) -> Анет баба 9/1 (Юнтека) -> Санжара Асфендиярова 11/169 -> Култегин 11а -> Культегин 13 (51) -> Ильяса Омарова 11 (28) -> Мухамедханова 6 -> Кайыма Мухамедханова 21/2/351 -> Айтматова 60 -> Е 429 14/1",
  "Зона 2: Жошы хан 23 -> Бокейхан 30 -> Аль-Фараби 7/3 (104) -> Е 117 33 (Юнтека) -> Е 761 4г -> Жандария (Алматы оплата) -> Е 652 10 -> Кабанбай Батыра 56 (107) -> Гейдара Алиева 2 (Доктор Дент) -> Туран 59/2 -> Толеби 25 (Юнтека) -> Туран 57/4 -> Толеби 16 -> Туран 50/3 (Юнтека) -> Туран 50/4",
  "Зона 3: Ахмета Байтурсынова 17 (26) -> Жумекена Нажимеденова 31 (236) -> Тәуелсіздік 40/7 -> Тәуелсіздік 21 -> Тәуелсіздік 25 (Юнтека) -> Момышулы 19 -> Жанайдара Жирентаева 14 (Башмачок) -> Мусрепова 7/1 (Юнтека) -> Габидена Мустафина 3 (35) -> Айнакол 55 -> Куйши Дина 42",
  "Зона 5: Абая 45/1 (кешеги) -> Абая 34 -> Әуезов 35 -> Есенберлина 7 -> Герцена (кешеги) -> Карталы 5 -> Шиликти 9 -> Жаяу Муса 22 -> Шаймердена Косшыгулулы 10/1 (103) -> Косшыгулулы 19 -> Серкебаева 33/4 (Юнтека) -> Сейфуллин 8 -> Жангельдина 8",
];

const delivery: Config = {
  appName: "DeliveryFlow",
  mainCollection: "delivery_orders",
  theme: {
    primaryColor: "#0f766e",
    secondaryColor: "#ea580c",
  },
  roles: allRoles,
  defaultRole: ROLES.CUSTOMER,
  statuses: [...DELIVERY_STATUS_VALUES],
  employeesVisibleTo: [ROLES.ADMIN],
  employeesEditableBy: [ROLES.ADMIN],
  screens: [
    {
      name: "DeliveryBoardScreen",
      type: "board",
      collection: "delivery_orders",
      roles: senderManagementRoles,
      showAdd: [ROLES.ADMIN, ROLES.SENDER],
      statusOptions,
      statusColors: STATUS_COLORS,
      defaultPresentation: "horizontal",
      defaultPresentationByRole: {
        [ROLES.ADMIN]: "horizontal",
        [ROLES.COURIER]: "table",
      },
      tableResponsive: {
        enabled: true,
      },
      reordering: managementRoles,
      orderBy: [{ field: "status", direction: "asc" }],
      quickStatus: {
        fieldName: "status",
        fieldNameByRole: {
          [ROLES.COURIER]: "status",
          [ROLES.ADMIN]: MANAGER_CONTACT_FIELD_NAME,
        },
        statusesByRole: {
          [ROLES.COURIER]: ["new", "not_delivered", "delivered"],
          [ROLES.ADMIN]: [
            MANAGER_CONTACT.HOME,
            MANAGER_CONTACT.NOT_HOME,
            MANAGER_CONTACT.NOT_CONTACTED,
          ],
        },
      },
      showBoardTypeSelector: false,
      where: [
        { field: "archived", operator: "==", value: false },
        {
          field: "courier.id",
          operator: "==",
          value: "uid",
          from: "user",
          applyToRoles: [ROLES.COURIER],
        },
        {
          field: "senderName",
          operator: "==",
          value: "senderName",
          from: "user",
          applyToRoles: [ROLES.SENDER],
        },
      ],
      filters: [
        {
          field: "deliveryZone",
          type: "dropdown",
          label: "Зона",
          value: "Не определена",
          options: ZONES,
          visibleBy: managementRoles,
        },
      ],
    },
    {
      name: "MyDeliveryRequestsScreen",
      type: "list",
      collection: "delivery_orders",
      roles: [ROLES.CUSTOMER],
      showAdd: [ROLES.CUSTOMER],
      presentation: "table",
      lazy: false,
      where: [
        { field: "archived", operator: "==", value: false },
        { field: "customerUid", operator: "==", value: "uid", from: "user" },
      ],
      orderBy: [{ field: "createdAt", direction: "desc" }],
    },
    {
      name: "DeliveryParserScreen",
      type: "deliveryParser",
      collection: "delivery_orders",
      icon: "text-box-plus-outline",
      roles: [ROLES.ADMIN, ROLES.SENDER],
    },
    {
      name: "DeliveryZoneMemoryScreen",
      type: "list",
      collection: "delivery_zone_memory",
      icon: "map-marker-radius-outline",
      roles: [ROLES.ADMIN],
      showAdd: [ROLES.ADMIN],
      presentation: "table",
      orderBy: [{ field: "updatedAt", direction: "desc" }],
      filters: [
        {
          field: "address",
          type: "search",
          label: "Поиск адреса",
          searchTermKeys: ["address", "zone", "note"],
        },
      ],
    },
    {
      name: "DeliveryZonesScreen",
      type: "list",
      collection: "delivery_zone_templates",
      icon: "map-marker-multiple-outline",
      roles: [ROLES.ADMIN],
      showAdd: [ROLES.ADMIN],
      presentation: "table",
      orderBy: [{ field: "zoneNumber", direction: "asc" }],
    },
    {
      name: "DeliveryStreetsScreen",
      type: "list",
      collection: "delivery_streets",
      icon: "sign-direction",
      roles: [ROLES.ADMIN],
      showAdd: [ROLES.ADMIN],
      presentation: "table",
      orderBy: [{ field: "name", direction: "asc" }],
      filters: [
        {
          field: "name",
          type: "search",
          label: "Поиск улицы",
          searchTermKeys: ["name", "zone", "note"],
        },
      ],
    },
    {
      name: "DeliveryCustomersScreen",
      type: "list",
      collection: "delivery_customers",
      icon: "account-multiple-outline",
      roles: [ROLES.ADMIN],
      showAdd: [ROLES.ADMIN],
      presentation: "table",
      orderBy: [{ field: "fullName", direction: "asc" }],
      filters: [
        {
          field: "fullName",
          type: "search",
          label: "Поиск клиента",
          searchTermKeys: ["fullName", "phone", "note"],
        },
      ],
    },
    {
      name: "DeliveryNotificationsScreen",
      type: "list",
      collection: "delivery_notifications_log",
      icon: "bell-outline",
      roles: [ROLES.ADMIN],
      showAdd: [],
      presentation: "table",
      orderBy: [{ field: "createdAt", direction: "desc" }],
    },
  ],
  database: [
    {
      name: "delivery_orders",
      deletableBy: [ROLES.ADMIN],
      defaultFields: [
        { name: "customerUid", key: "uid" },
        { name: "senderName", key: "senderName" },
      ],
      fields: [
        {
          name: "numberId",
          type: "numberId",
          label: "Номер заявки",
          list: [],
          form: [],
          board: [],
        },
        {
          name: "customerUid",
          type: "text",
          label: "UID клиента",
          list: [],
          formCreate: [],
          formEdit: [],
          board: [],
          details: [],
        },
        {
          name: "senderName",
          type: "dropdown",
          label: "Отправитель",
          list: [],
          options: SENDER_OPTIONS,
          defaultValue: "Кидсстор",
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER],
          formEdit: [ROLES.ADMIN],
          useUserValue: true,
          boardValueColorRules: [
            { text: "Кидсстор", color: "#0f766e" },
            { text: "Витамин 1", color: "#1d4ed8" },
            { text: "Витамин 2", color: "#9333ea" },
            { text: "Алматы", color: "#ea580c" },
            { text: "Абылайхана", color: "#17c86e" },
            { text: "Новый", color: "#4b5563" },
          ],
          board: [ROLES.ADMIN],
          details: [ROLES.ADMIN, ROLES.CUSTOMER],
          required: true,
        },
        {
          name: "customerName",
          type: "collectionPicker",
          label: "Получатель",
          collection: "delivery_customers",
          key: "fullName",
          secondKey: "phone",
          hideSecondKeyInDisplay: true,
          list: [ROLES.ADMIN, ROLES.CUSTOMER],
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER, ROLES.SENDER],
          formEdit: [ROLES.ADMIN],
          board: [ROLES.ADMIN],
          details: [ROLES.ADMIN, ROLES.CUSTOMER],
          required: true,
        },
        {
          name: "customerPhone",
          type: "phone",
          autoSelectFrom: "customerName.phone",
          label: "Телефон",
          list: allRoles,
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER, ROLES.SENDER],
          formEdit: [ROLES.ADMIN],
          board: [],
          required: true,
        },
        {
          name: "customerStreet",
          type: "collectionPicker",
          label: "Street",
          displayRowFlex: 0.5,
          hideLabel: true,
          collection: "delivery_streets",
          key: "name",
          secondKey: "zone",
          hideSecondKeyInDisplay: true,
          hideSecondKeyInDisplayForRoles: [ROLES.SENDER, ROLES.COURIER],
          list: allRoles,
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER, ROLES.SENDER],
          formEdit: [ROLES.ADMIN],
          board: senderManagementRoles,
          required: true,
        },
        {
          name: "houseNumber",
          type: "text",
          label: "House Number",
          displayRowFlex: 0.5,
          hideLabel: true,
          list: allRoles,
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER, ROLES.SENDER],
          formEdit: [ROLES.ADMIN],
          board: senderManagementRoles,
          required: true,
        },
        {
          name: "apartmentNumber",
          type: "text",
          label: "Apartment",
          list: allRoles,
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER, ROLES.SENDER],
          formEdit: [ROLES.ADMIN],
          board: [ROLES.ADMIN],
          required: false,
        },
        {
          name: "deliveryZone",
          type: "dropdown",
          label: "Зона",
          hideLabel: true,
          options: ZONES,
          defaultValue: "Не определена",
          list: [ROLES.ADMIN, ROLES.CUSTOMER],
          formCreate: [ROLES.ADMIN],
          formEdit: [ROLES.ADMIN],
          board: [],
          details: [ROLES.ADMIN, ROLES.CUSTOMER],
          autoSelectFrom: "customerStreet.zone",
        },
        {
          name: "priority",
          type: "dropdown",
          label: "Приоритет",
          hideLabel: true,
          displayRowFlex: 0.5,
          hideWhenValueInDisplay: ["normal"],
          options: PRIORITY_OPTIONS,
          boardValueColorRules: [
            { text: "normal", color: "#d32f2f" },
            "urgent",
            { text: "after_19", color: "#d32f2f" },
          ],
          defaultValue: "normal",
          list: allRoles,
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER],
          formEdit: [ROLES.ADMIN],
          board: [ROLES.COURIER],
        },
        {
          name: "status",
          type: "dropdown",
          label: "Статус",
          hideLabel: true,
          displayRowFlex: 0.5,
          options: [...DELIVERY_STATUS_VALUES],
          defaultValue: "new",
          boardValueColorRules: [
            { text: "delivered", color: "#16a34a" },
            "not_delivered",
          ],
          hideWhenValueInDisplayByRole: {
            [ROLES.COURIER]: ["new"],
          },
          sortRankMap: {
            new: 1,
            not_delivered: 2,
            delivered: 3,
          },
          list: allRoles,
          formCreate: [],
          formEdit: managementRoles,
          board: [ROLES.COURIER],
        },
        {
          name: "courier",
          type: "collectionPicker",
          label: "Курьер",
          collection: USERS_COLLECTION,
          key: "fullName",
          where: [{ field: "role", operator: "==", value: ROLES.COURIER }],
          showAdd: [ROLES.COURIER],
          list: allRoles,
          formCreate: [ROLES.ADMIN],
          formEdit: [ROLES.ADMIN],
          board: [ROLES.ADMIN],
        },
        {
          name: "additionalInfo",
          type: "textarea",
          label: "Доп. информация",
          list: allRoles,
          formCreate: [ROLES.ADMIN, ROLES.CUSTOMER, ROLES.SENDER],
          formEdit: [ROLES.ADMIN, ROLES.COURIER],
          board: [],
        },
        {
          name: MANAGER_CONTACT_FIELD_NAME,
          type: "dropdown",
          hideLabel: true,
          displayRowFlex: 0.5,
          options: MANAGER_CONTACT_OPTIONS,
          hideWhenValueInDisplayByRole: {
            [ROLES.COURIER]: [MANAGER_CONTACT.NOT_CONTACTED],
          },
          label: "Менеджер связался",
          boardValueColorRules: MANAGER_CONTACT_COLOR_RULES,
          defaultValue: MANAGER_CONTACT.NOT_CONTACTED,
          list: managementRoles,
          formCreate: [],
          formEdit: [ROLES.ADMIN],
          board: managementRoles,
          details: managementRoles,
        },
        {
          name: "rescheduledTo",
          type: "dateTime",
          label: "Перенос: дата/время",
          list: managementRoles,
          formCreate: [],
          formEdit: [ROLES.COURIER, ROLES.ADMIN],
          board: [],
        },
        {
          name: "customerNotificationText",
          type: "textarea",
          label: "Комментарий",
          valueDisplayMode: "bubble",
          valueDisplayTone: "default",
          hideWhenEmpty: true,
          list: managementRoles,
          formCreate: [],
          formEdit: [ROLES.COURIER, ROLES.ADMIN],
          board: [ROLES.ADMIN],
        },
        {
          name: "undeliveredComment",
          type: "textarea",
          label: "Undelivered comment",
          valueDisplayMode: "bubble",
          valueDisplayTone: "danger",
          hideWhenEmpty: true,
          list: managementRoles,
          formCreate: [],
          formEdit: [ROLES.COURIER, ROLES.ADMIN],
          board: [ROLES.ADMIN],
        },
        {
          name: "createdAt",
          type: "dateTime",
          label: "Создано",
          hideLabel: true,
          list: allRoles,
          form: [],
          board: [],
        },
        {
          name: "archived",
          type: "booleanCheckbox",
          label: "Архив",
          defaultValue: false,
          list: [],
          form: [],
          board: [],
        },
      ],
    },
    {
      name: "delivery_customers",
      deletableBy: [ROLES.ADMIN],
      fields: [
        {
          name: "fullName",
          type: "text",
          label: "Имя клиента",
          required: true,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN, ROLES.SENDER],
          board: [],
        },
        {
          name: "phone",
          type: "phone",
          label: "Телефон",
          required: false,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN, ROLES.SENDER],
          board: [],
        },
        {
          name: "note",
          type: "textarea",
          label: "Комментарий",
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN, ROLES.SENDER],
          board: [],
        },
        {
          name: "createdAt",
          type: "dateTime",
          label: "Создано",
          hideLabel: true,
          list: [ROLES.ADMIN],
          form: [],
          board: [],
        },
      ],
    },
    {
      name: "delivery_streets",
      deletableBy: [ROLES.ADMIN],
      fields: [
        {
          name: "name",
          type: "text",
          label: "Street",
          required: true,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN, ROLES.SENDER],
          board: [],
        },
        {
          name: "zone",
          type: "dropdown",
          label: "Зона по умолчанию",
          options: ZONES,
          defaultValue: "Не определена",
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN, ROLES.SENDER],
          formEdit: [ROLES.ADMIN],
          board: [],
        },
        {
          name: "note",
          type: "textarea",
          label: "Комментарий",
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN, ROLES.SENDER],
          board: [],
        },
        {
          name: "createdAt",
          type: "dateTime",
          label: "Создано",
          hideLabel: true,
          list: [ROLES.ADMIN],
          form: [],
          board: [],
        },
      ],
    },
    {
      name: "delivery_zone_memory",
      deletableBy: [ROLES.ADMIN],
      fields: [
        {
          name: "address",
          type: "text",
          label: "Адрес (эталон)",
          required: true,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN],
          board: [],
        },
        {
          name: "zone",
          type: "dropdown",
          label: "Зона",
          options: ZONES,
          required: true,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN],
          board: [],
        },
        {
          name: "note",
          type: "textarea",
          label: "Комментарий",
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN],
          board: [],
        },
        {
          name: "updatedAt",
          type: "dateTime",
          label: "Обновлено",
          hideLabel: true,
          list: [ROLES.ADMIN],
          form: [],
          board: [],
        },
      ],
    },
    {
      name: "delivery_zone_templates",
      deletableBy: [ROLES.ADMIN],
      fields: [
        {
          name: "zoneNumber",
          type: "dropdown",
          label: "Зона",
          options: ["Зона 1", "Зона 2", "Зона 3", "Зона 5"],
          required: true,
          list: managementRoles,
          form: [ROLES.ADMIN],
          board: [],
        },
        {
          name: "example",
          type: "dropdown",
          label: "Пример зоны",
          options: ZONE_EXAMPLES,
          required: true,
          list: managementRoles,
          form: [ROLES.ADMIN],
          board: [],
        },
        {
          name: "stopsRaw",
          type: "textarea",
          label: "Точки/комментарии",
          list: managementRoles,
          form: [ROLES.ADMIN],
          board: [],
        },
        {
          name: "createdAt",
          type: "dateTime",
          label: "Создано",
          hideLabel: true,
          list: managementRoles,
          form: [],
          board: [],
        },
      ],
    },
    {
      name: "delivery_notifications_log",
      deletableBy: [],
      fields: [
        {
          name: "orderId",
          type: "text",
          label: "ID заявки",
          list: managementRoles,
          form: [],
          board: [],
        },
        {
          name: "type",
          type: "dropdown",
          label: "Тип уведомления",
          options: [
            "До вас осталось: N доставок",
            "Доставка завершена ✅",
            "Не доставлено",
          ],
          list: managementRoles,
          form: [],
          board: [],
        },
        {
          name: "message",
          type: "textarea",
          label: "Сообщение",
          list: managementRoles,
          form: [],
          board: [],
        },
        {
          name: "createdAt",
          type: "dateTime",
          label: "Отправлено",
          hideLabel: true,
          list: managementRoles,
          form: [],
          board: [],
        },
      ],
    },
    {
      name: USERS_COLLECTION,
      fields: [
        {
          name: "role",
          type: "dropdown",
          label: "Role",
          options: allRoles,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN],
          board: [ROLES.ADMIN],
        },
        {
          name: "senderName",
          type: "dropdown",
          label: "Отправитель",
          options: SENDER_OPTIONS,
          list: [],
          form: [],
          board: [],
        },
        {
          name: "email",
          type: "text",
          label: "Email",
          readOnly: true,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN],
          board: [ROLES.ADMIN],
        },
        {
          name: "avatarUrl",
          type: "url",
          label: "Avatar",
          readOnly: true,
          list: [],
          form: [],
          board: [],
        },
        {
          name: "fullName",
          type: "text",
          label: "Full name",
          readOnly: true,
          list: [ROLES.ADMIN],
          form: [ROLES.ADMIN],
          board: [ROLES.ADMIN],
        },
      ],
    },
  ],
};

export default delivery;
